import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authService.me()
        .then(userData => {
          setUser(userData);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      // authService.login ya devuelve response.data
      const data = await authService.login(email, password);
      
      // Log para debugging (también en producción para diagnosticar)
      console.log('🔍 Respuesta del login recibida:', data);
      console.log('🔍 Tipo de data:', typeof data);
      console.log('🔍 Keys en data:', data ? Object.keys(data) : 'data es null/undefined');
      
      // Extraer user y token de la respuesta
      const userData = data.user;
      const token = data.token;
      
      if (!token) {
        console.error('No se recibió token. Respuesta completa:', data);
        return { 
          success: false, 
          error: 'No se recibió un token de autenticación. Verifica la configuración del servidor.' 
        };
      }
      
      if (!userData) {
        console.error('No se recibió user. Respuesta completa:', data);
        return { 
          success: false, 
          error: 'No se recibieron los datos del usuario.' 
        };
      }
      
      localStorage.setItem('token', token);
      setUser(userData);
      return { success: true };
    } catch (error) {
      console.error('Error en login:', error);
      console.error('Detalles del error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Manejar diferentes tipos de errores
      let errorMessage = 'Error al iniciar sesión. Verifica tu conexión.';
      
      if (error.response) {
        // Error de respuesta del servidor
        if (error.response.data) {
          if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (error.response.data.errors) {
            // Errores de validación de Laravel
            const firstError = Object.values(error.response.data.errors)[0];
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
